import { createMutation, createResource } from "@evergis/react-fetching";
import { CatalogResourceType, CopyResourceDc } from "@evergis/api";

import { api as evergisApi } from "../evergis/api";
import { promiseAllSettled } from "../utils/promiseAllSettled";
import { layerInfos, layerInfosWithDeps, layers } from "./layers";
import { projectInfosWithDeps, projects } from "./projects";
import { getTablesRefs, isLayerRef, tables } from "./tables";
import { invalidateResources, ResourceNames } from "./invalidations";
import { layerWithTable } from "./layerWithTable";

export const checkLimits = createResource(
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  (workspace?: string) => async api => ({
    data: await api.resourceCatalog.checkLimits({}),
  }),
  { injected: evergisApi },
);

export const copyResources = createMutation(
  (resources: CopyResourceDc[]) => async api => {
    return {
      data: await api.resourceCatalog.copyResources(resources),
    };
  },
  {
    injected: evergisApi,
    update: () => {
      projectInfosWithDeps.invalidateAll();
      layerInfosWithDeps.invalidateAll();
      projects.invalidateAll();
      layers.invalidateAll();
      tables.invalidateAll();
    },
  },
);

export const deleteResources = createMutation(
  ({ projects: resProjects = [], layers: resLayers = [], tables: resTables = [] }: ResourceNames) =>
    async api => {
      const projectsPromises = resProjects.map(name => api.projects.remove(name));
      const layersPromises = resLayers.map(name => api.layers.remove(name));
      const tablesPromises = resTables.map(name => api.tables.remove(name));
      const tableRefs = resLayers.length === 0 ? await getTablesRefs(resTables, isLayerRef) : [];

      return {
        data: {
          results: await promiseAllSettled([...projectsPromises, ...layersPromises, ...tablesPromises]),
          invalidateProjects: resProjects,
          invalidateLayers: [...resLayers, ...tableRefs],
          invalidateTables: resTables,
        },
      };
    },
  {
    injected: evergisApi,
    update: ({ invalidateProjects, invalidateLayers, invalidateTables }) => {
      invalidateProjects.length && projects.invalidateAll();
      invalidateLayers.length && layers.invalidateAll();
      invalidateTables.length && tables.invalidateAll();

      invalidateResources(invalidateProjects, invalidateLayers, invalidateTables);

      layerInfos.invalidateAll();
      projectInfosWithDeps.invalidateAll();
      layerInfosWithDeps.invalidateAll();
      layerWithTable.invalidateAll();
    },
  },
);

const noop = () => null;

export const resource = createResource(
  ({ name, resourceType }: { name: string; resourceType: CatalogResourceType }) =>
    async api => {
      const METHODS_MAP = {
        [CatalogResourceType.Layer]: api.layers.findOne,
        [CatalogResourceType.Table]: api.tables.findOne,
        [CatalogResourceType.Map]: api.projects.findOne,
      };

      const getMethod = METHODS_MAP[resourceType] || noop;

      return {
        data: await getMethod(name),
      };
    },
  { injected: evergisApi },
);
